import type { BolusEvent } from "@tandemdiabetes/sugarmate-api-react-client"
import { BolusState } from "@tandemdiabetes/sugarmate-api-react-client"
import { usePolyglot } from "src/contexts"
import { useDisplayTime } from "src/hooks"
import { BolusInformation } from "src/types"
import { serverInsulinToUserInsulin } from "src/utils"

const prefix = "components.event_modal.bolus."

export function useBolusInformationValue(
  bolusInfo: BolusInformation,
  bolusEvent: BolusEvent,
): string | null {
  const { bolus } = bolusEvent
  const polyglot = usePolyglot()
  const displayTime = useDisplayTime()
  const languageTag = polyglot.locale()

  switch (bolusInfo) {
    case BolusInformation.InitiatedAt:
      return displayTime(bolusEvent.started_at ?? "")
    case BolusInformation.StoppedAt:
      if (bolus.state !== BolusState.Stopped) {
        return null
      }
      return displayTime(bolusEvent.ended_at ?? "")
    case BolusInformation.TotalRequestedUnits:
      if (!bolus.insulin_total_requested) {
        return null
      }
      if (
        (bolus.state === BolusState.Completed || bolus.state === BolusState.Stopped) &&
        bolus.insulin_total_requested === bolus.insulin_total_delivered
      ) {
        return null
      }
      return polyglot.t(
        "unit.num_units_abbreviated",
        {
          units: serverInsulinToUserInsulin(bolus.insulin_total_requested).toLocaleString(languageTag),
        },
      )
    case BolusInformation.CorrectionBolus:
      if (!bolus.insulin_correction_bolus) {
        return null
      }
      if (bolus.insulin_correction_bolus === bolus.insulin_total_delivered) {
        return null
      }
      return polyglot.t(
        "unit.num_units_abbreviated",
        {
          units: serverInsulinToUserInsulin(bolus.insulin_correction_bolus).toLocaleString(languageTag),
        },
      )
    case BolusInformation.BolusNow:
      if (!bolus.insulin_bolus_now) {
        return null
      }
      return polyglot.t(
        "unit.num_units_abbreviated",
        {
          units: serverInsulinToUserInsulin(bolus.insulin_bolus_now).toLocaleString(languageTag),
        },
      )
    case BolusInformation.BolusLater:
      if (!bolus.insulin_bolus_later) {
        return null
      }
      return polyglot.t(
        "unit.num_units_abbreviated",
        {
          units: serverInsulinToUserInsulin(bolus.insulin_bolus_later).toLocaleString(languageTag),
        },
      )
    case BolusInformation.RequestedDuration: {
      if (!bolus.duration_in_min) {
        return null
      }
      const hours = Math.floor(bolus.duration_in_min / 60)
      const minutes = bolus.duration_in_min % 60

      const hoursString = polyglot.t(
        "time.hr",
        hours,
      )

      const minutesString = polyglot.t(
        "time.min",
        minutes,
      )

      if (hours > 0 && minutes > 0) {
        return polyglot.t(
          `${prefix}hours_and_minutes`,
          {
            hours: hoursString,
            minutes: minutesString,
          },
        )
      }

      if (hours > 0) {
        return hoursString
      }

      return minutesString
    }
    case BolusInformation.CarbsEntered:
      if (!bolus.carbs) {
        return null
      }
      return polyglot.t(
        "unit.num_grams",
        { grams: bolus.carbs },
      )
    case BolusInformation.InsulinEntered:
      if (!bolus.insulin_pump_entered) {
        return null
      }
      return polyglot.t(
        "unit.num_units_abbreviated",
        {
          units: serverInsulinToUserInsulin(bolus.insulin_pump_entered).toLocaleString(languageTag),
        },
      )
    case BolusInformation.PumpCalculated:
      if (bolus.insulin_pump_calculated == null) {
        return null
      }
      return polyglot.t(
        "unit.num_units_abbreviated",
        {
          units: serverInsulinToUserInsulin(bolus.insulin_pump_calculated).toLocaleString(languageTag),
        },
      )
    default:
      return null
  }
}
